import { useId } from 'react';
import { type FieldPath, type FieldValues, useController } from 'react-hook-form';
import { cn } from '../utils';

export interface FormFieldOwnProps<T extends FieldValues, N extends FieldPath<T>> {
  name: N;
  label?: string;
  helperText?: string;
  className?: string;
}

export function useFormField<T extends FieldValues, N extends FieldPath<T>>({
  name,
  label,
  helperText,
  className,
}: FormFieldOwnProps<T, N>) {
  const { field, fieldState } = useController<T, N>({ name });

  const { error } = fieldState;

  const id = useId();

  const fieldProps = {
    ...field,
    id,
    value: field.value ?? '',
    'aria-invalid': (!!error || undefined) as true | undefined,
    'aria-describedby': error ? `${id}-error` : helperText ? `${id}-helper` : undefined,
    className: cn(error && 'border-error', className),
  };

  const formFieldProps = { id, label, error, helperText };

  return { fieldProps, formFieldProps, fieldState } as const;
}
